import React from 'react';
import { Language } from '../types/kundali';
import { AdvancedLifeAnalysisResult, PhalaBhutaWindow } from '../types/advancedLifeAnalysis';
import { getTimingConfluence, calculateProjectedAgeWindow } from '../utils/presentationHelper';
import { Calendar, Clock, CheckCircle2, AlertCircle, Compass, Zap, Sparkles } from 'lucide-react';

interface PhalaBhutaTimelineProps {
  analysisResult: AdvancedLifeAnalysisResult;
  lang: Language;
  dobString?: string;
  className?: string;
}

export const PhalaBhutaTimeline: React.FC<PhalaBhutaTimelineProps> = ({
  analysisResult,
  lang,
  dobString = '1990-05-15',
  className = ''
}) => {
  const isHi = lang === 'hi';
  const { phalaBhutaWindows } = analysisResult;

  const windows: Array<{ window: PhalaBhutaWindow; rankLabelEn: string; rankLabelHi: string; isPrimary: boolean }> = [
    {
      window: phalaBhutaWindows.primary,
      rankLabelEn: 'Strongest Period',
      rankLabelHi: 'मुख्य फल-प्रदाता काल',
      isPrimary: true
    }
  ];

  if (phalaBhutaWindows.alternate) {
    windows.push({
      window: phalaBhutaWindows.alternate,
      rankLabelEn: 'Favorable Secondary Period',
      rankLabelHi: 'अनुकूल द्वितीयक काल',
      isPrimary: false
    });
  }

  if (phalaBhutaWindows.secondBest) {
    windows.push({
      window: phalaBhutaWindows.secondBest,
      rankLabelEn: 'Developing Period',
      rankLabelHi: 'विकासशील काल',
      isPrimary: false
    });
  }

  return (
    <div
      id="phala-bhuta-timeline"
      className={`bg-white dark:bg-[#202523] border border-[#E2DED5] dark:border-[#3B433F] rounded-2xl p-5 sm:p-6 shadow-xs space-y-5 ${className}`}
    >
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-[#E2DED5] dark:border-[#3B433F] pb-3">
        <div>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-astro-secondary dark:text-[#86A7A0]" />
            <h3 className="text-sm sm:text-base font-bold uppercase tracking-wider text-[#263633] dark:text-[#F2F1EB]">
              {isHi ? 'प्रमुख जीवन काल' : 'Key Life Periods'}
            </h3>
          </div>
          <p className="text-xs text-[#52635F] dark:text-[#B9C0BB] mt-0.5">
            {isHi
              ? 'दशा-भुक्ति एवं गोचर संमिलन के आधार पर वर्गीकृत समय अंतराल'
              : 'Key windows identified by dasha-bhukti and transit confluence.'}
          </p>
        </div>
      </div>

      {/* 3 Timing Windows Grid (Desktop: 3 cols or responsive) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {windows.map(({ window: win, rankLabelEn, rankLabelHi, isPrimary }, idx) => {
          const confluence = getTimingConfluence(win.confidenceScore, lang);
          const ageStr = calculateProjectedAgeWindow(dobString, win.startDate, win.endDate, lang);

          return (
            <div
              key={idx}
              className={`p-4 sm:p-5 rounded-xl border flex flex-col justify-between space-y-3 transition-all ${
                isPrimary
                  ? 'bg-astro-secondary-surface dark:bg-[#1C2220] border-2 border-astro-secondary/40 dark:border-[#86A7A0]/50 shadow-xs'
                  : 'bg-[#FDFCF9]/50 dark:bg-[#202523]/50 border-[#E2DED5] dark:border-[#3B433F]'
              }`}
            >
              <div>
                {/* Header Tag & Confluence Stars */}
                <div className="flex flex-wrap items-center justify-between gap-1.5 mb-2">
                  <span className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${
                    isPrimary
                      ? 'bg-astro-secondary text-white shadow-2xs'
                      : 'bg-[#E2DED5] dark:bg-[#3B433F] text-[#263633] dark:text-[#F2F1EB]'
                  }`}>
                    {isHi ? rankLabelHi : rankLabelEn}
                  </span>

                  {/* Confluence Rating */}
                  <div className="flex items-center gap-1 text-xs font-bold">
                    <span className="text-astro-accent dark:text-[#B7A17A] font-mono">{confluence.stars}</span>
                    <span className={`text-[10px] ${confluence.textColor}`}>{isHi ? confluence.labelHi : confluence.labelEn}</span>
                  </div>
                </div>

                {/* Date Window */}
                <div className="space-y-1 my-2">
                  <div className="text-base sm:text-lg font-bold font-serif text-[#263633] dark:text-[#F2F1EB]">
                    {win.startDate} <span className="text-xs font-sans text-[#52635F]">{isHi ? 'से' : 'to'}</span> {win.endDate}
                  </div>

                  {/* Projected Native Age Pill */}
                  {ageStr && (
                    <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-astro-primary bg-astro-bg border border-astro-border px-2 py-0.5 rounded shadow-2xs">
                      <Calendar className="w-3 h-3 text-astro-primary shrink-0" />
                      <span>{ageStr}</span>
                    </div>
                  )}
                </div>

                {/* Dasha Lords */}
                <div className="flex items-center gap-1.5 text-xs text-[#263633] dark:text-[#F2F1EB] my-1.5">
                  <span className="font-semibold">{isHi ? 'दशा स्वामी:' : 'Dasha Lords:'}</span>
                  <span className="px-2 py-0.5 rounded bg-white dark:bg-[#202523] border border-[#E2DED5] dark:border-[#3B433F] font-bold text-[#263633] dark:text-[#F2F1EB]">
                    {win.mahadashaLord}–{win.antardashaLord}
                  </span>
                </div>

                {/* Astrological Mechanism */}
                <p className="text-xs text-[#52635F] dark:text-[#B9C0BB] leading-relaxed font-normal pt-1">
                  {isHi ? win.astrologicalMechanismHi : win.astrologicalMechanismEn}
                </p>
              </div>

              {/* Supporting Points */}
              <div className="pt-2.5 border-t border-[#E2DED5]/80 dark:border-[#3B433F]/80 space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#52635F] dark:text-[#B9C0BB] block">
                  {isHi ? 'यह काल क्यों महत्वपूर्ण है:' : 'Why this period stands out:'}
                </span>
                <div className="flex flex-wrap gap-1">
                  {win.supportingEvidence.slice(0, 2).map((ev, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 text-[10px] bg-white dark:bg-[#202523] border border-[#E2DED5] dark:border-[#3B433F] text-[#263633] dark:text-[#F2F1EB] px-2 py-0.5 rounded"
                    >
                      <CheckCircle2 className="w-2.5 h-2.5 text-astro-secondary dark:text-[#86A7A0]" />
                      <span>{ev}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
